import React, { ChangeEvent, ReactNode, useContext, useEffect, useRef, useState } from "react";
import Grid from "@material-ui/core/Grid/Grid";
import { Box, createStyles, makeStyles } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import HotTable from "@handsontable/react";
import CustomDialog from "../CustomDialog";
import CustomButton from "../../Button/CustomButton";
import ParameterValue from "../../../api/models/test-series/ParameterValue";
import EnumParameterValue from "../../../api/models/test-series/EnumParameterValue";
import NumberParameterValue from "../../../api/models/test-series/NumberParameterValue";
import TextParameterValue from "../../../api/models/test-series/TextParameterValue";
import CustomTextField from "../../Input/CustomTextField";
import CustomDropdown from "../../Input/CustomDropdown";
import Parameter from "../../../api/models/test-series/Parameter";
import NumberParameter from "../../../api/models/test-series/NumberParameter";
import EnumParameter from "../../../api/models/test-series/EnumParameter";
import {
  handleParameter,
  mapToModel,
  NumberParameterValueModel,
  ParameterValueModel,
} from "./ParameterListDialogModel";
import CustomAlert from "../../Alert/CustomAlert";
import { tesaGrey1 } from "../../../styles/colors";
import MultiParamUnitSelector from "./MultiParamUnitSelector";
import ActionTemplateDropdown from "./ActionTemplateDropdown";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import ActionTemplate from "../../../api/models/test-series/ActionTemplate";
import TableActionsTable from "./TableActionsTable";
import ActionValueTableRow from "../../../api/models/test-series/ActionValueTableRow";
import InfoBox from "../../InfoBox";
import replaceDecimalComma from "../../../functions/replaceDecimalComma.function";
import { noSeparatorDecimalCommaTransformed } from "../../../helper/YupValidationHelpers";
import { ConfirmationDialogContext } from "../ConfirmationDialog/ConfirmationDialogContext";
import { CONFIRMATION_DIALOG_NO_THOUSANDS_SEPARATOR } from "../ConfirmationDialog/ConfirmationDialog.configurations";
import DECIMAL_OR_THOUSANDS_SEPARATOR from "../../../constants/regex/decimal-or-thousands-separator";

const useStyles = makeStyles(() =>
  createStyles({
    alert: {
      marginBottom: "16px",
    },
    header: {
      textTransform: "uppercase",
    },
    includeLabel: {
      opacity: 0.5,
      textAlign: "right",
    },
    subHeadline: {
      paddingBottom: "8px",
      color: tesaGrey1,
    },
  }),
);

const INFO_BOX_TEXT =
  "Min Value: minimal value, lower limit. *Value: target value, center value. *Max Value: maximum value, upper limit.";

export interface ParameterListDialogProps {
  onClose: () => void;
  onSave: (value: ParameterValue[], rows?: ActionValueTableRow[]) => void;
  open: boolean;
  parameters: Parameter[];
  parameterValues: ParameterValue[];
  title: string;
  disabled?: boolean;
  fetchActionTemplates?: () => Promise<ActionTemplate[]>;
  hintText?: string;
  hasUncontrolledParameters?: boolean;
  hasMinAndMaxValue?: boolean;
  rows?: ActionValueTableRow[];
  rowParameters?: Parameter[];
  sharedUnitParams?: ParameterValue[];
}

const ParameterListDialog = (props: ParameterListDialogProps): JSX.Element => {
  const {
    onClose,
    open,
    parameters,
    parameterValues,
    rows,
    rowParameters,
    title,
    onSave,
    hasMinAndMaxValue,
    hasUncontrolledParameters,
    disabled,
    fetchActionTemplates,
    hintText,
    sharedUnitParams,
  } = props;

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const classes = useStyles();
  const [actionTemplateName, setActionTemplateName] = useState<string>("");
  const [actionTemplates, setActionTemplates] = useState<ActionTemplate[]>([]);
  const [hasCheckboxColumn, setHasCheckboxColumn] = useState(false);
  const [hasNumberField, setHasNumberField] = useState(false);
  const [shareUnitSelected, setShareUnitSelected] = useState<ParameterValue[]>([]);
  const [tableActionRows, setTableActionRows] = useState<ActionValueTableRow[] | undefined>();
  const hotRef = useRef<HotTable>(null);

  const { showErrorSnackbar, showSnackbar } = useContext(ErrorSnackbarContext);
  const paramListFormRef: React.Ref<FormikProps<{ [key: string]: string }>> = useRef(null);
  const hasActionTemplates = !!actionTemplates?.length;
  const hasTableActions = !!rowParameters?.length;
  const hasSharedUnitParams = !!sharedUnitParams?.length;
  const models = parameters.map((parameter, index) =>
    mapToModel(
      index,
      parameter,
      parameterValues
        .filter((pv) => {
          if (pv.parameter.id && parameter.id) {
            return pv.parameter.id === parameter.id;
          }
          if (pv.parameter.tmbId && parameter.tmbId) {
            return pv.parameter.tmbId === parameter.tmbId;
          }
          return false;
        })
        .pop(),
    ),
  );

  const determineActionParameter = (model: ParameterValueModel) => {
    const regex = /include/;
    return regex.test(model.uncontrolledId);
  };

  const generateRows = (rowParams?: Parameter[], hotValues?: any[]): ActionValueTableRow[] | undefined => {
    if (!rowParams || !hotValues) {
      return;
    }
    const rowsFromHotVals = [] as ActionValueTableRow[];
    hotValues.forEach((rowData) => {
      let unitColumns = 0;
      const paramVals = rowParams.map((rp, paramIdx) => {
        const valueIdx = paramIdx + unitColumns;
        const paramValue = {
          action: true,
          parameter: rp,
          type: rp.type,
          value: rowData[valueIdx],
        };

        if (rp.type === "NUMBER" && rp.unit) {
          (paramValue as NumberParameterValue).unitCode = rowData[valueIdx + 1];
          unitColumns += 1;
        }

        return paramValue;
      });
      rowsFromHotVals.push({ parameterValues: paramVals });
    });
    return rowsFromHotVals;
  };

  const generateValues = (newParameterValues: ParameterValue[]): ParameterValue[] =>
    shareUnitSelected.length ? [...newParameterValues, ...shareUnitSelected] : newParameterValues;

  const handleActionTemplateChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = event.target.value as string;
    const actionValue = actionTemplates?.find((t) => t.name === name)?.value;
    if (!paramListFormRef.current) {
      return;
    }
    const newFormState = { ...paramListFormRef.current.values };
    actionValue?.action?.parameters?.forEach((p: Parameter, index: number) => {
      const vals = actionValue?.parameterValues?.find((pv: ParameterValue) => pv.parameter.tmbId === p.tmbId);
      const fieldIdValue = `ParameterListDialog_row-${index}-value`;
      const fieldIdUnit = `ParameterListDialog_row-${index}-unit`;
      // TODO clean up types
      newFormState[fieldIdValue] =
        String((vals as EnumParameterValue | NumberParameterValue | TextParameterValue)?.value) ||
        newFormState[fieldIdValue];
      newFormState[fieldIdUnit] = (vals as NumberParameterValue)?.unitCode || newFormState[fieldIdUnit];
    });
    paramListFormRef.current.setValues(newFormState);

    if (actionValue?.rows.length) {
      // change reference to trigger update on reselection of identical template
      setTableActionRows([...actionValue.rows]);
    }
    setActionTemplateName(name);
  };

  const resetSelectedActionTemplate = () => {
    if (actionTemplateName) {
      setActionTemplateName("");
    }
  };

  const focusHotIfFirstElement = () => {
    if (models.length === 0) {
      setTimeout(() => {
        hotRef.current?.hotInstance?.selectCell(0, 0);
      }, 100);
    }
  };

  useEffect(() => {
    if (fetchActionTemplates) {
      fetchActionTemplates()
        .then((templates) => {
          setActionTemplates(templates);
        })
        .catch((err: any) => {
          const reason = err?.response?.data?.message || err?.message;
          showErrorSnackbar(`Error fetching action templates: ${reason}`);
        });
    }

    const numberOfActionParameters = models.filter((model) => determineActionParameter(model)).length;

    if (numberOfActionParameters > 0 || hasUncontrolledParameters) {
      setHasCheckboxColumn(true);
    } else {
      setHasCheckboxColumn(false);
    }

    setHasNumberField(models.filter((model) => model.type === "NUMBER").length > 0);

    setShareUnitSelected([]);
    focusHotIfFirstElement();
    return () => {
      setActionTemplateName("");
      setActionTemplates([]);
    };
  }, [open]);

  useEffect(() => {
    if (rows && rows.length > 0) {
      setTableActionRows(rows);
    }
    return () => setTableActionRows(undefined);
  }, [open, rows]);

  const noSortEdges: Array<[string, string]> = [];
  const validationSchema: { [key: string]: Yup.Schema<unknown> } = {};

  models.forEach((m) => {
    handleParameter(m, {
      number: (nm) => {
        if (hasMinAndMaxValue) {
          noSortEdges.push([nm.minValueId, nm.minValueId]);
          noSortEdges.push([nm.minValueId, nm.id]);
          noSortEdges.push([nm.minValueId, nm.maxValueId]);
          noSortEdges.push([nm.id, nm.minValueId]);
          noSortEdges.push([nm.id, nm.id]);
          noSortEdges.push([nm.id, nm.maxValueId]);
          noSortEdges.push([nm.maxValueId, nm.minValueId]);
          noSortEdges.push([nm.maxValueId, nm.id]);
          noSortEdges.push([nm.maxValueId, nm.maxValueId]);
          validationSchema[nm.maxValueId] = Yup.string().when(
            [nm.minValueId, nm.id, nm.maxValueId],
            (minValue: string, value: string, maxValue: string) => {
              if (!minValue && !value) {
                return noSeparatorDecimalCommaTransformed.required("At least one value has to be specified.");
              }
              const compareTo = value || minValue;
              if (compareTo >= maxValue) {
                return noSeparatorDecimalCommaTransformed.numericStringMin(
                  compareTo,
                  `Max. should be greater than ${compareTo} or empty.`,
                );
              }

              return noSeparatorDecimalCommaTransformed.notRequired();
            },
          );
          validationSchema[nm.minValueId] = Yup.string().when(
            [nm.minValueId, nm.id, nm.maxValueId],
            (minValue: string, value: string, maxValue: string) => {
              if (!value && !maxValue) {
                return noSeparatorDecimalCommaTransformed.required("At least one value has to be specified.");
              }
              const compareTo = value || maxValue;
              if (minValue >= compareTo) {
                return noSeparatorDecimalCommaTransformed.numericStringMax(
                  compareTo,
                  `Min. should be less than ${compareTo} or empty.`,
                );
              }

              return noSeparatorDecimalCommaTransformed.notRequired();
            },
          );
          validationSchema[nm.id] = Yup.string().when(
            [nm.minValueId, nm.id, nm.maxValueId],
            (minValue: any, value: any, maxValue: any) => {
              if (!minValue && !maxValue) {
                return noSeparatorDecimalCommaTransformed.required("At least one value has to be specified.");
              }

              if (minValue && !maxValue && minValue >= value) {
                return noSeparatorDecimalCommaTransformed.numericStringMin(
                  minValue,
                  `Value should be greater than ${minValue} or empty.`,
                );
              }

              if (!minValue && maxValue && value >= maxValue) {
                return noSeparatorDecimalCommaTransformed.numericStringMax(
                  maxValue,
                  `Value should be less than ${maxValue} or empty.`,
                );
              }

              return noSeparatorDecimalCommaTransformed.notRequired();
            },
          );
        } else {
          validationSchema[nm.id] = Yup.mixed().when(`${m.uncontrolledId}`, {
            is: true,
            then: noSeparatorDecimalCommaTransformed.required("Please fill out this field."),
            otherwise: Yup.mixed(),
          });
        }

        validationSchema[nm.unitId] = Yup.mixed().when(`${m.uncontrolledId}`, {
          is: true,
          then: Yup.string().required("Please select an option"),
          otherwise: Yup.mixed(),
        });
      },
      enum: () => {
        validationSchema[m.id] = Yup.string().when(`${m.uncontrolledId}`, {
          is: true,
          then: Yup.string().required("Please select an option"),
          otherwise: Yup.string().nullable(),
        });
      },
      text: () => {
        validationSchema[m.id] = Yup.string().when(`${m.uncontrolledId}`, {
          is: true,
          then: Yup.string().required("Please fill out this field"),
          otherwise: Yup.string().nullable(),
        });
      },
    });
  });

  const renderForm = (formikProps: FormikProps<{ [key: string]: string }>): ReactNode => {
    const { errors, handleChange, touched, values } = formikProps;
    const handleValueChange = (e: any) => {
      if (actionTemplateName) {
        setActionTemplateName("");
      }
      handleChange(e);
    };

    return (
      <Form>
        <Box pb={4}>
          <Grid container justify="flex-start" direction="row" spacing={4}>
            {models.map((m, index) => (
              <React.Fragment key={m.id}>
                {handleParameter<ReactNode>(m, {
                  text: () => (
                    <>
                      <Grid item xs={hasCheckboxColumn ? 11 : 12}>
                        <CustomTextField
                          autoFocus={index === 0}
                          fullWidth
                          error={!!errors[m.id] && !!touched[m.id]}
                          helperText={touched[m.id] ? errors[m.id] : ""}
                          id={m.id}
                          label={m.name}
                          value={values[m.id]}
                          onChange={handleValueChange}
                          disabled={!values[m.uncontrolledId] || disabled}
                        />
                      </Grid>
                      {hasCheckboxColumn && (
                        <Grid item xs={1} container alignItems="center" justify="flex-end">
                          <Checkbox
                            disabled={disabled}
                            name={m.uncontrolledId}
                            value={values[m.uncontrolledId]}
                            checked={!!values[m.uncontrolledId]}
                            onChange={handleChange}
                          />
                        </Grid>
                      )}
                    </>
                  ),
                  enum: (em) => (
                    <>
                      <Grid item xs={hasCheckboxColumn ? 11 : 12}>
                        <CustomDropdown
                          autoFocus={index === 0}
                          fullWidth
                          error={!!errors[m.id] && !!touched[m.id]}
                          name={m.id}
                          labelText={m.name}
                          value={values[m.id]}
                          onChange={handleValueChange}
                          disabled={!values[m.uncontrolledId] || disabled}
                        >
                          {em.values.map((v) => (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          ))}
                        </CustomDropdown>
                      </Grid>
                      {hasCheckboxColumn && (
                        <Grid item xs={1} container alignItems="center" justify="flex-end">
                          <Checkbox
                            disabled={disabled}
                            name={m.uncontrolledId}
                            value={values[m.uncontrolledId]}
                            checked={!!values[m.uncontrolledId]}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                          />
                        </Grid>
                      )}
                    </>
                  ),
                  number: (nm) => (
                    <>
                      {hasMinAndMaxValue ? (
                        <>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              autoFocus={index === 0}
                              fullWidth
                              error={!!errors[nm.minValueId] && !!touched[nm.minValueId]}
                              helperText={touched[nm.minValueId] ? errors[nm.minValueId] : ""}
                              id={nm.minValueId}
                              label="Min. Value"
                              value={values[nm.minValueId]}
                              onChange={handleValueChange}
                              disabled={!values[nm.uncontrolledId] || disabled}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              error={!!errors[nm.id] && !!touched[nm.id]}
                              helperText={touched[nm.id] ? errors[nm.id] : ""}
                              id={nm.id}
                              label="Value"
                              value={values[nm.id] || ""}
                              onChange={handleValueChange}
                              disabled={!values[nm.uncontrolledId] || disabled}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              error={!!errors[nm.maxValueId] && !!touched[nm.maxValueId]}
                              helperText={touched[nm.maxValueId] ? errors[nm.maxValueId] : ""}
                              id={nm.maxValueId}
                              label="Max. Value"
                              value={values[nm.maxValueId]}
                              onChange={handleValueChange}
                              disabled={!values[nm.uncontrolledId] || disabled}
                            />
                          </Grid>
                        </>
                      ) : (
                        <Grid item lg={6} md={6} sm={6} xs={6}>
                          <CustomTextField
                            autoFocus={index === 0}
                            fullWidth
                            error={!!errors[nm.id] && !!touched[nm.id]}
                            helperText={touched[nm.id] ? errors[nm.id] : ""}
                            id={nm.id}
                            label={nm.name}
                            value={values[nm.id]}
                            onChange={handleValueChange}
                            disabled={!values[nm.uncontrolledId] || disabled}
                          />
                        </Grid>
                      )}
                      <Grid item xs={hasCheckboxColumn ? 5 : hasMinAndMaxValue ? 3 : 6}>
                        <CustomDropdown
                          fullWidth
                          error={!!errors[nm.unitId] && !!touched[nm.unitId]}
                          name={nm.unitId}
                          labelText="Unit"
                          value={values[nm.unitId]}
                          onChange={(e) => {
                            handleValueChange(e);
                          }}
                          disabled={!values[nm.uncontrolledId] || disabled}
                        >
                          {nm.units.map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </CustomDropdown>
                      </Grid>
                      {hasCheckboxColumn && (
                        <Grid container alignItems="center" justify="flex-end" item xs={1}>
                          <Checkbox
                            disabled={disabled}
                            name={nm.uncontrolledId}
                            value={values[nm.uncontrolledId]}
                            checked={!!values[nm.uncontrolledId]}
                            onChange={handleChange}
                          />
                        </Grid>
                      )}
                    </>
                  ),
                })}
              </React.Fragment>
            ))}
            {hasTableActions && (
              <TableActionsTable
                disabled={!!disabled}
                ref={hotRef}
                resetSelectedActionTemplate={resetSelectedActionTemplate}
                rows={tableActionRows}
                rowParameters={rowParameters}
                showErrorSnackbar={showErrorSnackbar}
              />
            )}
          </Grid>
          {hasSharedUnitParams && !disabled && (
            <Box marginTop={1}>
              <MultiParamUnitSelector sharedUnitParams={sharedUnitParams} onSelectionChanged={setShareUnitSelected} />
            </Box>
          )}
        </Box>
        <Grid container item direction="row" justify="flex-end">
          <CustomButton
            primary
            text={disabled ? "Close" : "Apply"}
            type={disabled ? "button" : "submit"}
            onClick={disabled ? onClose : undefined}
          />
        </Grid>
      </Form>
    );
  };

  const initial: { [key: string]: string } = {};
  models.forEach((m) => {
    handleParameter(m, {
      number: (nm) => {
        initial[nm.id] = nm.value;
        initial[nm.unitId] = nm.unitCode;
        // TODO clean up types
        initial[nm.uncontrolledId] = nm.include as unknown as string;
        if (hasMinAndMaxValue) {
          initial[nm.minValueId] = nm.minValue;
          initial[nm.maxValueId] = nm.maxValue;
        }
      },
      enum: () => {
        initial[m.id] = m.value;
        // TODO clean up types
        initial[m.uncontrolledId] = m.include as unknown as string;
      },
      text: () => {
        initial[m.id] = m.value;
        // TODO clean up types
        initial[m.uncontrolledId] = m.include as unknown as string;
      },
    });
  });

  const showInfoIcon = parameterValues && parameterValues.length > 0 ? parameterValues[0].type === "NUMBER" : false;

  const confirmNoThousandsSeparator = (
    valueModels: ParameterValueModel[],
    values: { [key: string]: string },
    regex: RegExp,
  ) => {
    const candidates: string[] = [];
    valueModels.forEach((m) => {
      if (m.type === "NUMBER") {
        candidates.push(values[(m as NumberParameterValueModel).id] || "");
        candidates.push(values[(m as NumberParameterValueModel).maxValueId] || "");
        candidates.push(values[(m as NumberParameterValueModel).minValueId] || "");
      }
    });
    return candidates.some((c) => regex.test(c));
  };

  const handleSubmit = (formikValues: { [key: string]: string }): void => {
    const newParameterValues = models.map((m) =>
      handleParameter<ParameterValue>(m, {
        number: (nm) => {
          const minMaxValues =
            hasMinAndMaxValue && formikValues[m.uncontrolledId]
              ? {
                  minValue: formikValues[nm.minValueId]
                    ? parseFloat(replaceDecimalComma(formikValues[nm.minValueId]))
                    : null,
                  maxValue: formikValues[nm.maxValueId]
                    ? parseFloat(replaceDecimalComma(formikValues[nm.maxValueId]))
                    : null,
                }
              : {};
          const numberValue: NumberParameterValue = {
            // FIXME parameters ordered like backend is returning them to avoid "dirty" parameter values
            type: nm.type,
            id: nm.parameterValueId,
            parameter: nm.parameter as NumberParameter,
            uncontrolled: !formikValues[m.uncontrolledId],
            value:
              formikValues[m.uncontrolledId] && formikValues[nm.id]
                ? parseFloat(replaceDecimalComma(formikValues[nm.id]))
                : null,
            unitCode: formikValues[nm.unitId],
            ...minMaxValues,
          };
          return numberValue as ParameterValue;
        },
        enum: (em) => {
          const enumValue: EnumParameterValue = {
            // FIXME parameters ordered like backend is returning them to avoid "dirty" parameter values
            type: em.type,
            id: em.parameterValueId,
            parameter: em.parameter as EnumParameter,
            uncontrolled: !formikValues[m.uncontrolledId],
            value: formikValues[em.id],
          };
          return enumValue as ParameterValue;
        },
        text: (tm) => {
          const enumValue: TextParameterValue = {
            // FIXME parameters ordered like backend is returning them to avoid "dirty" parameter values
            type: tm.type,
            id: tm.parameterValueId,
            parameter: tm.parameter,
            uncontrolled: !formikValues[m.uncontrolledId],
            value: formikValues[tm.id],
          };
          return enumValue as ParameterValue;
        },
      }),
    );
    // FIXME sort back to backend sorting by id to avoid "dirty" parameter values
    newParameterValues.sort((a, b) => (a.id || 0) - (b.id || 0));

    const hotInstance = hotRef.current?.hotInstance;
    if (hotInstance) {
      hotInstance.validateCells((valid) => {
        if (valid) {
          onSave(generateValues(newParameterValues), generateRows(rowParameters, hotInstance.getData()));
          showSnackbar("Parameter values updated", "success");
        }
      });
    } else {
      onSave(generateValues(newParameterValues));
      showSnackbar("Parameter values created", "success");
    }
  };

  return (
    <CustomDialog fullWidth maxWidth="md" onClose={(): void => onClose()} open={open}>
      <InfoBox compress hidden={!showInfoIcon} text={INFO_BOX_TEXT}>
        <h2 style={{ marginBottom: "0px" }} className={classes.header}>
          {title}
        </h2>
      </InfoBox>
      <Box marginBottom="1rem" />
      {hintText && <CustomAlert severity="info" className={classes.alert} text={hintText} />}
      {hasActionTemplates && (
        <ActionTemplateDropdown
          handleChange={handleActionTemplateChange}
          selectedTemplate={actionTemplateName}
          templates={actionTemplates}
        />
      )}
      {hasCheckboxColumn && <div className={classes.includeLabel}>Include</div>}
      {hasMinAndMaxValue && hasNumberField && <div className={classes.subHeadline}>Fill in at least one value</div>}
      <Formik
        enableReinitialize
        initialValues={initial}
        innerRef={paramListFormRef}
        validationSchema={Yup.object().shape({ ...validationSchema }, noSortEdges)}
        onSubmit={(formikValues): void => {
          if (confirmNoThousandsSeparator(models, formikValues, DECIMAL_OR_THOUSANDS_SEPARATOR)) {
            showConfirmationDialog(CONFIRMATION_DIALOG_NO_THOUSANDS_SEPARATOR)
              .then(() => handleSubmit(formikValues))
              .catch(() => {
                // do nothing
              });
          } else {
            handleSubmit(formikValues);
          }
        }}
      >
        {renderForm}
      </Formik>
    </CustomDialog>
  );
};

export default ParameterListDialog;
